import { baseBackendUrl } from './baseService';
import {
    GroupDetail,
    GroupEvent,
    GroupEventRequest,
    GroupMember,
    GroupRequest,
    GroupSummary,
    GroupUpdate,
    GroupUpdateRequest,
} from '../types/group';

const groupsUrl = `${baseBackendUrl}/api/groups`;

// Thrown for non-2xx responses so callers can tell a 404 (group missing or
// inactive) or a 403 (not your group) apart from other failures.
export class GroupApiError extends Error {
    status: number;

    constructor(message: string, status: number) {
        super(message);
        this.status = status;
    }
}

// The token is sent whenever we have one, even on anonymous endpoints, because
// the server uses it to fill in the caller's membershipStatus / canManage.
const buildHeaders = (token?: string, json = false): HeadersInit => {
    const headers: Record<string, string> = {};
    if (json) headers['Content-Type'] = 'application/json';
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
};

const send = async (
    path: string,
    errorMessage: string,
    token?: string,
    method: string = 'GET',
    body?: unknown
): Promise<Response> => {
    const response = await fetch(`${groupsUrl}${path}`, {
        method,
        headers: buildHeaders(token, body !== undefined),
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) throw new GroupApiError(errorMessage, response.status);
    return response;
};

export const groupService = {
    // Public
    getGroups: async (token?: string): Promise<GroupSummary[]> =>
        (await send('', 'Failed to fetch groups', token)).json(),

    getAnyActive: async (): Promise<{ hasActive: boolean }> =>
        (await send('/any-active', 'Failed to check for groups')).json(),

    getGroup: async (id: number | string, token?: string): Promise<GroupDetail> =>
        (await send(`/${id}`, 'Failed to fetch group', token)).json(),

    join: async (id: number, token: string): Promise<{ status: string }> =>
        (await send(`/${id}/join`, 'Failed to request to join group', token, 'POST')).json(),

    leave: async (id: number, token: string): Promise<void> => {
        await send(`/${id}/membership`, 'Failed to update membership', token, 'DELETE');
    },

    // Management
    getManaged: async (token: string): Promise<GroupSummary[]> =>
        (await send('/managed', 'Failed to fetch managed groups', token)).json(),

    createGroup: async (request: GroupRequest, token: string): Promise<GroupSummary> =>
        (await send('', 'Failed to create group', token, 'POST', request)).json(),

    updateGroup: async (id: number, request: GroupRequest, token: string): Promise<void> => {
        await send(`/${id}`, 'Failed to update group', token, 'PUT', request);
    },

    deleteGroup: async (id: number, token: string): Promise<void> => {
        await send(`/${id}`, 'Failed to delete group', token, 'DELETE');
    },

    setActive: async (id: number, isActive: boolean, token: string): Promise<void> => {
        await send(`/${id}/active`, 'Failed to update group status', token, 'PUT', { isActive });
    },

    // Members
    getMembers: async (id: number | string, token: string): Promise<GroupMember[]> =>
        (await send(`/${id}/members`, 'Failed to fetch members', token)).json(),

    decideMember: async (id: number | string, userId: string, approve: boolean, token: string): Promise<void> => {
        await send(`/${id}/members/${encodeURIComponent(userId)}`, 'Failed to update request', token, 'PUT', { approve });
    },

    removeMember: async (id: number | string, userId: string, token: string): Promise<void> => {
        await send(`/${id}/members/${encodeURIComponent(userId)}`, 'Failed to remove member', token, 'DELETE');
    },

    setMemberAdmin: async (id: number | string, userId: string, isAdmin: boolean, token: string): Promise<void> => {
        await send(`/${id}/members/${encodeURIComponent(userId)}/admin`, 'Failed to update leader', token, 'PUT', { isAdmin });
    },

    // Updates
    createUpdate: async (id: number | string, request: GroupUpdateRequest, token: string): Promise<GroupUpdate> =>
        (await send(`/${id}/updates`, 'Failed to create update', token, 'POST', request)).json(),

    editUpdate: async (id: number | string, updateId: number, request: GroupUpdateRequest, token: string): Promise<void> => {
        await send(`/${id}/updates/${updateId}`, 'Failed to save update', token, 'PUT', request);
    },

    deleteUpdate: async (id: number | string, updateId: number, token: string): Promise<void> => {
        await send(`/${id}/updates/${updateId}`, 'Failed to delete update', token, 'DELETE');
    },

    // Events
    createEvent: async (id: number | string, request: GroupEventRequest, token: string): Promise<GroupEvent> =>
        (await send(`/${id}/events`, 'Failed to create event', token, 'POST', request)).json(),

    editEvent: async (id: number | string, eventId: number, request: GroupEventRequest, token: string): Promise<void> => {
        await send(`/${id}/events/${eventId}`, 'Failed to save event', token, 'PUT', request);
    },

    deleteEvent: async (id: number | string, eventId: number, token: string): Promise<void> => {
        await send(`/${id}/events/${eventId}`, 'Failed to delete event', token, 'DELETE');
    },
};
